import {
    SCol,
    SIcon,
    SInputExtended,
    SPagination,
    SRow,
    STableContainer,
} from "@avalara/skylab-sdk/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    fetchAllBucketUsers,
    fetchBucketUsersByBucketId,
} from "../../../../app/bucketsSlice/bucketActions";
import {
    selectAssignedUsers,
    selectIsLoading,
    selectIsUsersLoading,
    selectUsers,
} from "../../../../app/bucketsSlice/bucketSelectors";
import { selectSession, updateRowCountPreference } from "../../../../app/sessionSlice";
import type { AppDispatch } from "../../../../app/types";
import { getPageTitle } from "../../../../shared/sessionUtility";

interface Bucket {
    name: string;
    id?: string;
}

interface LocationState {
    bucket?: Bucket;
}

/**
 * Type guard to safely check if location.state matches LocationState structure
 */
function isLocationState(state: unknown): state is LocationState {
    return (
        !!state &&
        typeof state === "object" &&
        (!("bucket" in state) ||
            !state.bucket ||
            (typeof state.bucket === "object" &&
                "name" in state.bucket &&
                typeof state.bucket.name === "string"))
    );
}

import {
    SInputData,
    SInputExtendedCustomEvent,
    SPaginateData,
    SPaginationCustomEvent,
} from "@avalara/skylab-sdk/loader";
import type { SessionState } from "../../../../app/sessionSlice/types";
import featureFlag from "../../../../featureToggler/featureFlag";
import { useIsAuthorized } from "../../../../hooks/useIsAuthorized";
import commonRoutesConfig from "../../../navigation/commonRoutesConfig";
import { getGeneratedPathForRoute } from "../../../navigation/utils";
import NoData from "../../../search/NoData";
import RecordCount from "../../../search/RecordCount";
import type { NoDataProps, RecordCountProps } from "../../../search/types";
import Loader from "../../../sharedComponents/Loader";
import "../buckets.css";
import AssignUser from "./AssignUser";
import RemoveSelectedUsersDialog from "./RemoveSelectedUsersDialog";
import type { User } from "./types";

export const countLabel = "users";
// TODO - Remove these asserted components once the original components are refactored to use .tsx extension
const NoDataComponent = NoData as React.ComponentType<NoDataProps>;

const BucketUserGrid = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const { bucketId } = useParams<{ bucketId: string }>();
    const session = useSelector(selectSession, shallowEqual) as SessionState;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(session.rowsPerPage || 20);
    const isUserLoading = useSelector(selectIsLoading) as boolean;
    const isAssignedUsersLoading = useSelector(selectIsUsersLoading) as boolean;
    const allUsersRaw = useSelector(selectUsers) as User[];
    const assignedUsersRaw = useSelector(selectAssignedUsers) as User[];
    const canAddEditDeleteBucketsUsers = useIsAuthorized(
        featureFlag.settings.addEditDeleteBucketsUsers
    );
    // Ensure we always have arrays
    const allUsers = Array.isArray(allUsersRaw) ? allUsersRaw : [];
    const assignedUsers = Array.isArray(assignedUsersRaw) ? assignedUsersRaw : [];

    // Safely extract bucket name from location state
    const bucketName = isLocationState(location.state) ? location.state.bucket?.name : undefined;
    const filteredAssignedUsers = assignedUsers.filter(
        (user: User) =>
            user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const availableUsers = allUsers.filter(
        (user: User) => !assignedUsers.some((assignedUser: User) => assignedUser.id === user.id)
    );
    const totalRecords = filteredAssignedUsers.length;

    // Ensure pagination boundaries are respected
    const { validatedRowsPerPage, validatedStartIndex, paginatedAssignedUsers } = useMemo(() => {
        const validatedRowsPerPage = Math.max(1, rowsPerPage);
        const validatedCurrentPage = Math.max(
            1,
            Math.min(currentPage, Math.ceil(totalRecords / validatedRowsPerPage) || 1)
        );
        const validatedStartIndex = (validatedCurrentPage - 1) * validatedRowsPerPage;

        const paginatedAssignedUsers: User[] =
            totalRecords > 0
                ? filteredAssignedUsers.slice(
                      validatedStartIndex,
                      validatedStartIndex + validatedRowsPerPage
                  )
                : [];

        return { validatedRowsPerPage, validatedStartIndex, paginatedAssignedUsers };
    }, [filteredAssignedUsers, totalRecords, currentPage, rowsPerPage]);

    useEffect(() => {
        dispatch(fetchBucketUsersByBucketId(bucketId));
        dispatch(fetchAllBucketUsers());
    }, [dispatch, bucketId]);

    useEffect(() => {
        return () => {
            setSelectedUsers(new Set());
            setSearchTerm("");
        };
    }, []);

    // Handle pagination validation when data changes
    useEffect(() => {
        if (filteredAssignedUsers.length > 0 && rowsPerPage > 0) {
            const maxPages = Math.ceil(filteredAssignedUsers.length / rowsPerPage);
            if (currentPage > maxPages) {
                setCurrentPage(maxPages);
                setSelectedUsers(new Set<number>());
            }
        } else if (filteredAssignedUsers.length === 0 && currentPage > 1) {
            setCurrentPage(1);
            setSelectedUsers(new Set<number>());
        }
    }, [filteredAssignedUsers.length, rowsPerPage, currentPage]);

    // Refetch data when users are assigned/removed to ensure data consistency
    useEffect(() => {
        if (bucketId && assignedUsers.length > 0) {
            setSelectedUsers(new Set<number>());

            const maxPages = Math.ceil(assignedUsers.length / rowsPerPage);
            if (currentPage > maxPages && maxPages > 0) {
                setCurrentPage(maxPages);
            }
        }
    }, [assignedUsers.length, bucketId, rowsPerPage, currentPage]);

    const handleUserSelection = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const userId: number = parseInt(e.target.value, 10);
            const newSelection = new Set<number>(selectedUsers);

            if (e.target.checked) {
                newSelection.add(userId);
            } else {
                newSelection.delete(userId);
            }

            setSelectedUsers(newSelection);
        },
        [selectedUsers]
    );

    const handleSelectAll = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            if (e.target.checked) {
                setSelectedUsers(
                    new Set<number>(paginatedAssignedUsers.map((user: User) => user.id))
                );
            } else {
                setSelectedUsers(new Set<number>());
            }
        },
        [paginatedAssignedUsers]
    );

    const handleRemoveSelectedUsers = useCallback((): void => {
        if (selectedUsers.size > 0) {
            setIsRemoveDialogOpen(true);
        }
    }, [selectedUsers]);

    const handleRemoveUser = useCallback((userId: number): void => {
        setSelectedUsers(new Set<number>([userId]));
        setIsRemoveDialogOpen(true);
    }, []);

    const handleSearchInput = useCallback((e: SInputExtendedCustomEvent<SInputData>): void => {
        setSearchTerm(e.detail.value.trim());
        setCurrentPage(1);
        setSelectedUsers(new Set<number>());
    }, []);

    const handleClearSearch = useCallback((): void => {
        setSearchTerm("");
        setCurrentPage(1);
        setSelectedUsers(new Set<number>());
    }, []);

    const handlePaginate = useCallback(
        (e: SPaginationCustomEvent<SPaginateData>): void => {
            const { currentPage, rowsPerPage: newRowsPerPage, flag } = e.detail;

            if (flag && flag !== "") {
                if (newRowsPerPage !== session.rowsPerPage) {
                    setRowsPerPage(newRowsPerPage);
                    dispatch(updateRowCountPreference(newRowsPerPage));
                }
                setCurrentPage(currentPage);
                setSelectedUsers(new Set<number>());
            }
        },
        [session.rowsPerPage, dispatch]
    );

    const renderActionButtons = (row: User) => (
        <button
            type="button"
            className="icon-leading text-blue-medium unstyled-button"
            onClick={() => handleRemoveUser(row.id)}
            title="Remove user"
            disabled={!canAddEditDeleteBucketsUsers}>
            <SIcon name="trash" className="margin-right-xs" aria-label="Remove" />
        </button>
    );

    const handleBackToSettings = useCallback(() => {
        navigate(getGeneratedPathForRoute(commonRoutesConfig.settingsBuckets));
    }, [navigate]);

    const hasSearchTerm = searchTerm.trim().length > 0;
    const hasNoSearchResults = hasSearchTerm && filteredAssignedUsers.length === 0;

    const table = hasNoSearchResults ? (
        <NoDataComponent showGenNoDataMsg={true} />
    ) : (
        <STableContainer tabIndex={0}>
            <table>
                <thead>
                    <tr>
                        <th className="divider-end pad-all-sm checkbox-column-width">
                            <input
                                type="checkbox"
                                className="unlabeled"
                                onChange={handleSelectAll}
                                checked={
                                    paginatedAssignedUsers.length > 0 &&
                                    selectedUsers.size === paginatedAssignedUsers.length &&
                                    paginatedAssignedUsers.every((user: User) =>
                                        selectedUsers.has(user.id)
                                    )
                                }
                                aria-label="Select all users"
                            />
                        </th>
                        <th className="text-left text-align-center">Assigned user</th>
                        <th className="text-right"></th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedAssignedUsers.map((row: User) => (
                        <tr key={row.id}>
                            <td className="divider-end pad-all-sm checkbox-column-width">
                                <input
                                    type="checkbox"
                                    className="unlabeled"
                                    id={`user-${row.id}`}
                                    value={row.id}
                                    checked={selectedUsers.has(row.id)}
                                    onChange={handleUserSelection}
                                    aria-label={`Select ${row.userName}`}
                                />
                            </td>
                            <td className="text-left text-align-center">{`${row.userName}(${row.email})`}</td>
                            <td className="text-right text-align-center">
                                {renderActionButtons(row)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </STableContainer>
    );

    return (
        <React.Fragment>
            <Helmet>
                <title>{dispatch(getPageTitle("Bucket Users"))}</title>
            </Helmet>
            <h1 className="line-height-md margin-top-none">
                Manage users in {bucketName || "Bucket"}
            </h1>

            <AssignUser
                availableUsers={availableUsers}
                isUserLoading={isUserLoading}
                bucketId={bucketId}
            />

            {isAssignedUsersLoading || isUserLoading ? (
                <Loader loading={isAssignedUsersLoading || isUserLoading} />
            ) : !assignedUsers.length ? (
                <NoDataComponent bucket={true} />
            ) : (
                <>
                    <h4>Search for assigned users in the bucket</h4>
                    <SRow className="align-items-end margin-bottom-sm">
                        <SCol span="6">
                            <SInputExtended
                                inputId="userSearch"
                                aria-label="Search by user name"
                                type="search-live"
                                placeholder="Enter a name or email address"
                                onS-input={handleSearchInput}
                                onS-clear={handleClearSearch}
                                value={searchTerm}
                            />
                        </SCol>
                        <SCol span="6" className="text-right">
                            <button
                                type="button"
                                className="secondary icon-leading"
                                onClick={handleRemoveSelectedUsers}
                                disabled={selectedUsers.size < 1 || !canAddEditDeleteBucketsUsers}>
                                Remove selected users
                            </button>
                        </SCol>
                    </SRow>

                    <RecordCount
                        {...({
                            selectedCount: 0,
                            recordCount: totalRecords || 0,
                            recordName: countLabel,
                        } as RecordCountProps)}
                    />

                    {table}

                    {totalRecords > 0 ? (
                        <SPagination
                            className="margin-top-md"
                            rowsPerPage={validatedRowsPerPage}
                            totalRecords={totalRecords}
                            startIndex={validatedStartIndex}
                            onS-paginate={handlePaginate}
                        />
                    ) : null}

                    {isRemoveDialogOpen ? (
                        <RemoveSelectedUsersDialog
                            isRemoveDialogOpen={isRemoveDialogOpen}
                            setIsRemoveDialogOpen={setIsRemoveDialogOpen}
                            selectedUsers={selectedUsers}
                            setSelectedUsers={setSelectedUsers}
                            assignedUsers={assignedUsers}
                            bucketId={bucketId}
                        />
                    ) : null}
                </>
            )}

            <span className="flex justify-content-start">
                <button
                    className="secondary"
                    type="button"
                    data-testid="back-button"
                    onClick={handleBackToSettings}>
                    Back to bucket settings
                </button>
            </span>
        </React.Fragment>
    );
};

BucketUserGrid.displayName = "BucketUserGrid";
export default BucketUserGrid;
